import { defineStore } from 'pinia'
import { StatusType } from '@injectivelabs/utils/status'
import { toBigNumber } from '@injectivelabs/utils/big-number'
import {
  CHAIN_ID,
  DEFAULT_GAS_PRICE,
  ETHEREUM_CHAIN_ID
} from '@shared/utils/constant'
import { captureLazySentryException } from '@/app/providers/sentry/lazy'
import {
  MAX_SLIPPAGE,
  MIN_SLIPPAGE,
  DEFAULT_SLIPPAGE,
  DEFAULT_100_CHART_CANDLE_BAR_SPACING
} from '@/app/utils/constants'
import {
  NoticeBanner,
  TradingLayout,
  OrderbookLayout,
  TradingChartInterval
} from '@/types/enums'
import type { ChainId, EvmChainId } from '@injectivelabs/ts-types'
import type { Modal, TcCtaToast } from '@/types/enums'

const importAppStream = () => import('./stream')

function reportCancelAllStreamsError(error: unknown) {
  captureLazySentryException(error, {
    message: 'Failed to cancel app streams',
    attributes: {
      action: 'app.cancelAllStreams'
    }
  })
}

interface UserBasedState {
  modalsViewed: Modal[]
  seenStartTrading: boolean
  favoriteMarkets: string[]
  bannersViewed: NoticeBanner[]
  marketSlippageIdMap: Record<string, string>
  marketLeverageIdMap: Record<string, string>
  dontShowAgain: Array<Modal | TcCtaToast | NoticeBanner>

  preferences: {
    isHideBalances: boolean
    selectedLanguage: string
    chartShowPosition: boolean
    isOrderbookVisible: boolean
    playOrderFillSound: boolean
    thousandsSeparator: boolean
    chartZoomPreference: number
    tradingLayout: TradingLayout
    chartShowTradeHistory: boolean
    orderbookLayout: OrderbookLayout
    isAutoMarketCloseEnabled: boolean
    isRfqFastExecutionEnabled: boolean
    isRfqClientGatewayEnabled: boolean
    tradingChartInterval: TradingChartInterval
  }
}

type AppStoreState = {
  // App Settings
  chainId: ChainId

  gasPrice: string
  blockHeight: number
  busyActions: string[]
  evmChainId: EvmChainId

  // User settings
  userState: UserBasedState
}

const initialStateFactory = (): AppStoreState => ({
  blockHeight: 0,

  // App Settings
  chainId: CHAIN_ID,
  busyActions: [],
  evmChainId: ETHEREUM_CHAIN_ID,
  gasPrice: DEFAULT_GAS_PRICE.toString(),

  // User settings
  userState: {
    modalsViewed: [],
    bannersViewed: [],
    dontShowAgain: [],
    favoriteMarkets: [],
    seenStartTrading: false,
    marketSlippageIdMap: {},
    marketLeverageIdMap: {},

    preferences: {
      selectedLanguage: '',
      isHideBalances: false,
      chartShowPosition: true,
      isOrderbookVisible: false,
      thousandsSeparator: true,
      playOrderFillSound: false,
      isAutoMarketCloseEnabled: false,
      chartShowTradeHistory: true,
      tradingLayout: TradingLayout.Left,
      orderbookLayout: OrderbookLayout.Default,
      isRfqFastExecutionEnabled: true,
      isRfqClientGatewayEnabled: false,
      tradingChartInterval: TradingChartInterval['1h'],
      chartZoomPreference: DEFAULT_100_CHART_CANDLE_BAR_SPACING
    }
  }
})

export const useAppStore = defineStore('app', {
  state: (): AppStoreState => initialStateFactory(),
  getters: {
    isTradingDisabled: () => {
      const sharedJsonStore = useSharedJsonStore()
      const sharedAppConfigStore = useSharedAppConfigStore()

      return (
        sharedJsonStore.isPostUpgradeMode ||
        !!sharedAppConfigStore.appConfig?.disableTrading
      )
    },

    isWalletTransactionBusy: (state: AppStoreState) => {
      const sharedWalletStore = useSharedWalletStore()

      return (
        state.busyActions.length > 0 ||
        sharedWalletStore.queueStatus === StatusType.Loading
      )
    },

    slippageByMarketId: (state: AppStoreState) => {
      return (marketId: string): string => {
        const stored = state.userState?.marketSlippageIdMap?.[marketId]

        if (!stored) {
          return DEFAULT_SLIPPAGE.toFixed()
        }

        const storedInBigNumber = toBigNumber(stored)
        const isInRange =
          storedInBigNumber.gte(MIN_SLIPPAGE) &&
          storedInBigNumber.lte(MAX_SLIPPAGE)

        return isInRange ? stored : DEFAULT_SLIPPAGE.toFixed()
      }
    },

    leverageByMarketId: (state: AppStoreState) => {
      return (marketId: string): string | undefined => {
        return state.userState?.marketLeverageIdMap?.[marketId]
      }
    },

    favoriteMarkets: (state: AppStoreState) => {
      return state.userState.favoriteMarkets
    }
  },
  actions: {
    setGasPrice(gasPrice: string) {
      this.gasPrice = gasPrice
    },

    setBlockHeight(blockHeight: number) {
      this.blockHeight = blockHeight
    },

    setChainId(chainId: ChainId) {
      this.chainId = chainId
    },

    setEvmChainId(evmChainId: EvmChainId) {
      this.evmChainId = evmChainId
    },

    setUserState(userState: UserBasedState) {
      this.userState = userState
    },

    setBusyActions(busyActions: string[]) {
      this.busyActions = busyActions
    },

    streamBlockHeight: lazyPiniaAction(importAppStream, 'streamBlockHeight'),
    cancelBlockHeightStream: lazyPiniaAction(
      importAppStream,
      'cancelBlockHeightStream'
    ),

    async fetchGasPrice() {
      const appStore = useAppStore()
      const { fetchGasPrice } = await import('@shared/service/app/ethGasPrice')

      appStore.setGasPrice(await fetchGasPrice())
    },

    toggleFavoriteMarket(marketId: string) {
      const appStore = useAppStore()

      const cachedFavoriteMarkets = appStore.userState.favoriteMarkets

      const favoriteMarkets = cachedFavoriteMarkets.includes(marketId)
        ? cachedFavoriteMarkets.filter((m) => m !== marketId)
        : [marketId, ...cachedFavoriteMarkets]

      appStore.setUserState({
        ...appStore.userState,
        favoriteMarkets
      })
    },

    toggleHideBalances() {
      const appStore = useAppStore()

      appStore.setUserState({
        ...appStore.userState,
        preferences: {
          ...appStore.userState.preferences,
          isHideBalances: !appStore.userState.preferences.isHideBalances
        }
      })
    },

    setMarketSlippage(marketId: string, value: string) {
      const appStore = useAppStore()
      const parsed = toBigNumber(value)

      let finalSlippage: string

      if (parsed.isNaN()) {
        finalSlippage = DEFAULT_SLIPPAGE.toFixed()
      } else if (parsed.lt(MIN_SLIPPAGE)) {
        finalSlippage = MIN_SLIPPAGE.toFixed()
      } else if (parsed.gt(MAX_SLIPPAGE)) {
        finalSlippage = MAX_SLIPPAGE.toFixed()
      } else {
        finalSlippage = value
      }

      appStore.setUserState({
        ...appStore.userState,
        marketSlippageIdMap: {
          ...appStore.userState.marketSlippageIdMap,
          [marketId]: finalSlippage
        }
      })

      return finalSlippage
    },

    setMarketLeverage(marketId: string, leverageAmount: string) {
      const appStore = useAppStore()

      appStore.setUserState({
        ...appStore.userState,
        marketLeverageIdMap: {
          ...appStore.userState.marketLeverageIdMap,
          [marketId]: leverageAmount
        }
      })
    },

    setChartZoomPreference(number: number) {
      const appStore = useAppStore()

      appStore.setUserState({
        ...appStore.userState,
        preferences: {
          ...appStore.userState.preferences,
          chartZoomPreference: number
        }
      })
    },

    setSelectedLanguage(selectedLocale: string) {
      const appStore = useAppStore()

      appStore.setUserState({
        ...appStore.userState,
        preferences: {
          ...appStore.userState.preferences,
          selectedLanguage: selectedLocale
        }
      })
    },

    async cancelAllStreams() {
      try {
        const { streamRegistry } = await import('@/streams/registry')

        streamRegistry.unsubscribeAll()
      } catch (error) {
        reportCancelAllStreamsError(error)

        throw error
      }
    },

    toggleOrderbookVisibility() {
      const appStore = useAppStore()

      appStore.setUserState({
        ...appStore.userState,
        preferences: {
          ...appStore.userState.preferences,
          isOrderbookVisible: !appStore.userState.preferences.isOrderbookVisible
        }
      })
    },

    toggleOrderFillSound() {
      const appStore = useAppStore()

      appStore.setUserState({
        ...appStore.userState,
        preferences: {
          ...appStore.userState.preferences,
          playOrderFillSound: !appStore.userState.preferences.playOrderFillSound
        }
      })
    },

    toggleAutoMarketClose() {
      const appStore = useAppStore()

      appStore.setUserState({
        ...appStore.userState,
        preferences: {
          ...appStore.userState.preferences,
          isAutoMarketCloseEnabled:
            !appStore.userState.preferences.isAutoMarketCloseEnabled
        }
      })
    },

    toggleRfqGatewayPrefetch() {
      const appStore = useAppStore()

      appStore.setUserState({
        ...appStore.userState,
        preferences: {
          ...appStore.userState.preferences,
          isRfqFastExecutionEnabled:
            !appStore.userState.preferences.isRfqFastExecutionEnabled
        }
      })
    },

    toggleRfqClientGateway() {
      const appStore = useAppStore()
      const isRfqClientGatewayEnabled =
        !appStore.userState.preferences.isRfqClientGatewayEnabled

      appStore.setUserState({
        ...appStore.userState,
        preferences: {
          ...appStore.userState.preferences,
          isRfqClientGatewayEnabled
        }
      })

      if (!isRfqClientGatewayEnabled) {
        return
      }

      const walletStore = useWalletStore()

      walletStore.requestRfqClientGatewayFeeGrantInBackground()
    },

    reset() {
      const appStore = useAppStore()
      const initialState = initialStateFactory()

      const isStocksBannerViewed = appStore.userState.bannersViewed.find(
        (item) => item === NoticeBanner.Stocks
      )
      const userState = appStore.userState

      appStore.setChainId(initialState.chainId)
      appStore.setGasPrice(initialState.gasPrice)
      appStore.setEvmChainId(initialState.evmChainId)
      appStore.setBusyActions(initialState.busyActions)
      appStore.setBlockHeight(initialState.blockHeight)
      appStore.setUserState({
        ...initialState.userState,
        dontShowAgain: userState.dontShowAgain,
        favoriteMarkets: userState.favoriteMarkets,
        seenStartTrading: userState.seenStartTrading,
        bannersViewed: isStocksBannerViewed ? [NoticeBanner.Stocks] : [],
        preferences: {
          ...userState.preferences,
          selectedLanguage: userState.preferences.selectedLanguage
        }
      })
    }
  }
})
